import {
  ApprovalUserRef,
  ReportApprovalWorkflow,
  ReportESignatureRecord,
  SignoffRole,
  SignoffState,
} from 'types'

export const SIGNOFF_ROLE_ORDER: readonly SignoffRole[] = [
  SignoffRole.SUPERVISOR,
  SignoffRole.DATA_ANALYST,
  SignoffRole.REVIEWER,
]

export const SIGNOFF_ROLE_LABELS: Record<SignoffRole, string> = {
  [SignoffRole.SUPERVISOR]: 'Inspection Supervisor',
  [SignoffRole.DATA_ANALYST]: 'Data Analyst',
  [SignoffRole.REVIEWER]: 'Reviewer',
}

const SIGNOFF_FIELD_BY_ROLE: Record<
  SignoffRole,
  keyof Omit<SignoffState, 'lastRevision'>
> = {
  [SignoffRole.SUPERVISOR]: 'supervisorSignoff',
  [SignoffRole.DATA_ANALYST]: 'dataAnalystSignoff',
  [SignoffRole.REVIEWER]: 'reviewerSignoff',
}

export type ApprovalAssignmentDraft = ApprovalUserRef & {
  qualifications?: string
}

export interface ApprovalWorkflowDraft {
  requiredRoles: SignoffRole[]
  assignments: Partial<Record<SignoffRole, ApprovalAssignmentDraft>>
}

/** Dropped roles are patched as `null` (Rolodex deep-merges), so strip them on load. */
export function normalizeApprovalWorkflow(
  raw: ReportApprovalWorkflow | undefined
): ReportApprovalWorkflow | undefined {
  if (!raw) return undefined
  const assignments: ReportApprovalWorkflow['assignments'] = {}
  for (const role of SIGNOFF_ROLE_ORDER) {
    const assignment = raw.assignments?.[role]
    if (assignment) assignments[role] = assignment
  }
  return { ...raw, requiredRoles: raw.requiredRoles ?? [], assignments }
}

export type CurrentUserRef = Pick<ApprovalUserRef, 'userId' | 'email'>

/**
 * Assignees come from the all-users listing and the current user from the
 * current-user endpoint; match on id first and fall back to email in case the
 * two disagree on id format.
 */
export const isAssignee = (
  assignment: ApprovalUserRef | undefined,
  user: CurrentUserRef | undefined
) =>
  !!assignment &&
  !!user &&
  (assignment.userId === user.userId ||
    assignment.email.toLowerCase() === user.email.toLowerCase())

export const getSignatureForRole = (
  records: readonly ReportESignatureRecord[],
  role: SignoffRole
) => records.find((record) => record.role === role)

/**
 * Signatures chain inside one PDF, so none can be removed alone; assignments
 * freeze once anyone has signed and unfreeze when all signatures are voided.
 */
export const canEditAssignments = (
  records: readonly ReportESignatureRecord[]
) => records.length === 0

export const canManageWorkflow = (
  workflow: ReportApprovalWorkflow | undefined,
  userId: string | undefined,
  isAdmin: boolean
) =>
  isAdmin ||
  (!!userId && (!workflow || workflow.configuredBy.userId === userId))

/** Required roles assigned to `user` that still need their signature. */
export const getPendingSignatureRoles = (
  workflow: ReportApprovalWorkflow | undefined,
  records: readonly ReportESignatureRecord[],
  user: CurrentUserRef | undefined
): SignoffRole[] =>
  workflow
    ? workflow.requiredRoles.filter(
        (role) =>
          isAssignee(workflow.assignments[role], user) &&
          !getSignatureForRole(records, role)
      )
    : []

/** Why `user` cannot sign `role` right now, or undefined when they can. */
export function getSignBlocker(
  workflow: ReportApprovalWorkflow | undefined,
  records: readonly ReportESignatureRecord[],
  role: SignoffRole,
  user: CurrentUserRef | undefined
): string | undefined {
  const label = SIGNOFF_ROLE_LABELS[role]
  const assignment = workflow?.assignments[role]
  if (!workflow?.requiredRoles.includes(role) || !assignment) {
    return `No one is assigned as ${label}`
  }
  if (getSignatureForRole(records, role)) return `${label} has already signed`
  if (!isAssignee(assignment, user)) {
    return `Only ${assignment.name} can sign as ${label}`
  }
  return undefined
}

export function buildApprovalWorkflow(
  draft: ApprovalWorkflowDraft,
  existing: ReportApprovalWorkflow | undefined,
  configurer: ApprovalUserRef,
  now: string
): ReportApprovalWorkflow {
  const requiredRoles = SIGNOFF_ROLE_ORDER.filter((role) =>
    draft.requiredRoles.includes(role)
  )
  const assignments: ReportApprovalWorkflow['assignments'] = {}
  for (const role of requiredRoles) {
    const next = draft.assignments[role]
    if (!next) continue
    const previous = existing?.assignments[role]
    assignments[role] = {
      ...next,
      qualifications: next.qualifications || undefined,
      assignedAt: previous?.userId === next.userId ? previous.assignedAt : now,
    }
  }
  return {
    configuredBy: existing?.configuredBy ?? configurer,
    configuredAt: now,
    requiredRoles,
    assignments,
  }
}

/** Mirrors assignees onto `signoff`, which the PDF cover pages already print. */
export function deriveSignoffFromWorkflow(
  workflow: ReportApprovalWorkflow,
  existing: SignoffState | undefined,
  now: string
): SignoffState {
  const next: SignoffState = { lastRevision: existing?.lastRevision ?? 0 }
  for (const role of SIGNOFF_ROLE_ORDER) {
    const field = SIGNOFF_FIELD_BY_ROLE[role]
    const assignment = workflow.assignments[role]
    if (!assignment) {
      next[field] = null
      continue
    }
    const previous = existing?.[field]
    const qualifications = assignment.qualifications ?? ''
    const unchanged =
      previous?.name === assignment.name &&
      (previous.qualifications ?? '') === qualifications
    next[field] = {
      role,
      name: assignment.name,
      qualifications,
      timestamp: unchanged ? previous.timestamp : now,
    }
  }
  return next
}

export const isSignedDocumentCurrent = (
  latestDraftStamp: string | undefined,
  latestSignedStamp: string | undefined
) =>
  !!latestSignedStamp &&
  (!latestDraftStamp || latestSignedStamp >= latestDraftStamp)

export function getPublishBlocker(
  workflow: ReportApprovalWorkflow | undefined,
  records: readonly ReportESignatureRecord[],
  stamps: { latestDraft?: string; latestSigned?: string }
): string | undefined {
  if (!workflow?.requiredRoles.length) {
    return 'Configure approval roles before publishing'
  }
  const unsigned = workflow.requiredRoles.filter(
    (role) => !getSignatureForRole(records, role)
  )
  if (unsigned.length > 0) {
    return `Waiting on signatures: ${unsigned.map((role) => SIGNOFF_ROLE_LABELS[role]).join(', ')}`
  }
  if (!isSignedDocumentCurrent(stamps.latestDraft, stamps.latestSigned)) {
    return 'A newer draft was generated after signing. Void the signatures and sign the current draft.'
  }
  return undefined
}
